import { type DropdownSection } from '@hologram-dimension/dropdown-panel';
import { useMemo } from 'react';
import formatNumber from 'utils/numbers/formatNumber';
import useActivatePreflight from './Items/useActivatePreflight';
import useChangePlan from './Items/useChangePlan';
import useDeactivate from './Items/useDeactivate';
import useManageTags from './Items/useManageTags';
import useManageTunneling from './Items/useManageTunneling';
import usePauseSims from './Items/usePauseSims';
import useRenameSims from './Items/useRenameSims';
import useResumeSims from './Items/useResumeSims';
import useSendMessage from './Items/useSendMessage';
import useSetDataUsageAlert from './Items/useSetDataUsageAlert';
import useSetDataUsageLimit from './Items/useSetDataUsageLimit';
import useSimulateMessageToApi from './Items/useSimulateMessageToApi';
import useSwitchProfiles from './Items/useSwitchProfiles';
import useTransferToOrganization from './Items/useTransferToOrganization';
import type { DropdownItemEntry, ManageSimItemsProps } from './types';

type UseManageSimItems = (
  props: ManageSimItemsProps & { subtitle?: string }
) => Array<DropdownSection>;

const isDropdownItem = (item: DropdownItemEntry | false): item is DropdownItemEntry => !!item;

const itemsWithinLimit = (
  items: Array<DropdownItemEntry>,
  selectionCount: number,
  overLimitItems: Map<number, Array<DropdownItemEntry>>
) => {
  const withinLimitItems: Array<DropdownItemEntry> = [];
  items.forEach((item) => {
    if (item.limit && selectionCount > item.limit) {
      const section = overLimitItems.get(item.limit);
      if (!section) {
        overLimitItems.set(item.limit, [item]);
      } else {
        section.push(item);
      }
    } else {
      withinLimitItems.push(item);
    }
  });
  return withinLimitItems;
};

const useManageSimItems: UseManageSimItems = ({ subtitle, context, selectedSims, ...rest }) => {
  // Reconstruct the spread for use in hooks
  const itemProps = { context, selectedSims, ...rest };

  /* ===== Activation items ===== */

  const activatePreflight = useActivatePreflight(itemProps);

  const activationItems = useMemo<Array<DropdownItemEntry>>(
    () => [activatePreflight].filter<DropdownItemEntry>(isDropdownItem),
    [activatePreflight]
  );

  /* ===== Data usage items ===== */
  const pauseSims = usePauseSims(itemProps);
  const resumeSims = useResumeSims(itemProps);
  const switchProfiles = useSwitchProfiles(itemProps);

  const dataUsageItems = useMemo<Array<DropdownItemEntry>>(
    () => [pauseSims, resumeSims, switchProfiles].filter<DropdownItemEntry>(isDropdownItem),
    [pauseSims, resumeSims, switchProfiles]
  );

  /* ===== Organization/messaging items ===== */

  const manageTags = useManageTags(itemProps);
  const renameSims = useRenameSims(itemProps);
  const sendMessage = useSendMessage(itemProps);
  const simulateMessage = useSimulateMessageToApi(itemProps);

  const organizationItems = useMemo<Array<DropdownItemEntry>>(
    () =>
      [manageTags, renameSims, sendMessage, simulateMessage].filter<DropdownItemEntry>(
        isDropdownItem
      ),
    [manageTags, renameSims, sendMessage, simulateMessage]
  );

  /* ===== Data items ===== */
  const changePlan = useChangePlan(itemProps);
  const setDataUsageAlert = useSetDataUsageAlert(itemProps);
  const setDataUsageLimit = useSetDataUsageLimit(itemProps);
  const manageDeviceTunneling = useManageTunneling(itemProps);
  const transferToOrganization = useTransferToOrganization(itemProps);

  const dataItems = useMemo<Array<DropdownItemEntry>>(
    () =>
      [
        setDataUsageLimit,
        setDataUsageAlert,
        changePlan,
        manageDeviceTunneling,
        transferToOrganization,
      ].filter<DropdownItemEntry>(isDropdownItem),
    [
      changePlan,
      manageDeviceTunneling,
      setDataUsageAlert,
      setDataUsageLimit,
      transferToOrganization,
    ]
  );

  /* ===== Deactivate items ===== */
  const deactivate = useDeactivate(itemProps);

  const deactivateItems = useMemo<Array<DropdownItemEntry>>(
    () => [deactivate].filter<DropdownItemEntry>(isDropdownItem),
    [deactivate]
  );

  return useMemo(() => {
    const content: Array<DropdownSection> = [];
    const overLimitItems = new Map<number, Array<DropdownItemEntry>>();

    let hasSetSubtitle = false;
    const activationItemsInLimit = itemsWithinLimit(
      activationItems,
      selectedSims?.size ?? 0,
      overLimitItems
    );
    if (activationItemsInLimit.length > 0) {
      content.push({
        label: subtitle,
        items: activationItemsInLimit,
      });
      hasSetSubtitle = true;
    }

    const dataUsageItemsInLimit = itemsWithinLimit(
      dataUsageItems,
      selectedSims?.size ?? 0,
      overLimitItems
    );
    if (dataUsageItemsInLimit.length > 0) {
      content.push({
        label: hasSetSubtitle ? undefined : subtitle,
        items: dataUsageItemsInLimit,
      });
      hasSetSubtitle = true;
    }

    const organizationItemsInLimit = itemsWithinLimit(
      organizationItems,
      selectedSims?.size ?? 0,
      overLimitItems
    );
    if (organizationItemsInLimit.length > 0) {
      content.push({
        label: hasSetSubtitle ? undefined : subtitle,
        items: organizationItemsInLimit,
      });
      hasSetSubtitle = true;
    }

    const dataItemsInLimit = itemsWithinLimit(dataItems, selectedSims?.size ?? 0, overLimitItems);
    if (dataItemsInLimit.length > 0) {
      content.push({
        label: hasSetSubtitle ? undefined : subtitle,
        items: dataItemsInLimit,
      });
      hasSetSubtitle = true;
    }

    const deactivateItemsInLimit = itemsWithinLimit(
      deactivateItems,
      selectedSims?.size ?? 0,
      overLimitItems
    );
    if (deactivateItemsInLimit.length > 0) {
      content.push({
        label: hasSetSubtitle ? undefined : subtitle,
        items: deactivateItemsInLimit,
      });
      hasSetSubtitle = true;
    }

    if (overLimitItems.size > 0) {
      [...overLimitItems.entries()]
        .sort((a, b) => b[0] - a[0])
        .forEach(([limit, items]) => {
          content.push({
            label: `Only available for ${formatNumber(limit)} SIMs or less`,
            items: items.map((item) => ({ ...item, disabled: true })),
          });
        });
    }

    return content;
  }, [
    activationItems,
    dataItems,
    dataUsageItems,
    deactivateItems,
    organizationItems,
    selectedSims?.size,
    subtitle,
  ]);
};

export default useManageSimItems;
